import { Button, ButtonProps, defineStyleConfig, forwardRef, useStyleConfig } from '@chakra-ui/react';

import Icon, { IconName } from '@/components/atomic/v1/icon/Icon';

export const interactiveTagTheme = defineStyleConfig({
  baseStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    border: '2px solid',
    backgroundColor: 'background/white',
    borderRadius: '5px',
    px: { base: 2, lg: 3 },
    py: { base: 1, lg: 2 },
    textStyle: { base: 'button.small', lg: 'button.regular' },
    lineHeight: { base: 1.25, lg: 1.2 },
    minHeight: { base: 8, lg: 10 },
    height: 'auto'
  },
  variants: {
    grey: {
      color: 'status/disabled/text',
      borderColor: 'grey.50',
      _hover: {
        borderColor: 'grey.50',
        backgroundColor: 'grey.10'
      },
      _focus: {
        borderColor: 'grey.50',
        backgroundColor: 'status/disabled/bg'
      },
      _active: {
        backgroundColor: 'status/disabled/bg',
        borderColor: 'transparent'
      }
    },
    green: {
      color: 'status/success/text',
      borderColor: 'green.50',
      _hover: {
        borderColor: 'green.50',
        backgroundColor: 'grass.25'
      },
      _focus: {
        borderColor: 'green.50',
        backgroundColor: 'status/success/bg'
      },
      _active: {
        backgroundColor: 'status/success/bg',
        borderColor: 'transparent'
      }
    },
    blue: {
      color: 'status/information/text',
      borderColor: 'blue.50',
      _hover: {
        borderColor: 'blue.50',
        backgroundColor: 'sky.25'
      },
      _focus: {
        borderColor: 'blue.50',
        backgroundColor: 'status/information/bg'
      },
      _active: {
        backgroundColor: 'status/information/bg',
        borderColor: 'transparent'
      }
    }
  }
});

type InteractiveTagVariant = 'grey' | 'green' | 'blue';

interface InteractiveTagProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon'> {
  children: React.ReactNode;
  variant: InteractiveTagVariant;
  leftIcon?: IconName;
  rightIcon?: IconName;
  isActive?: boolean;
}

const InteractiveTag = forwardRef<InteractiveTagProps, 'button'>(
  ({ children, variant, leftIcon, rightIcon, isActive = false, ...rest }: InteractiveTagProps, ref) => {
    const styles = useStyleConfig('InteractiveTag', { variant });

    return (
      <Button
        __css={{
          ...styles,
          ...(isActive ? interactiveTagTheme.variants![variant]._active : {})
        }}
        ref={ref}
        {...rest}
      >
        {leftIcon && <Icon name={leftIcon} size={24} />}
        {children}
        {rightIcon && <Icon name={rightIcon} size={18} />}
      </Button>
    );
  }
);

export default InteractiveTag;
